/**
 * Pure rules for deciding whether an axios failure might mean "the API is not there",
 * and how long to wait between reconnect probes.
 *
 * Split out of helpers/apiStatus.js so the judgement can be unit-tested without a
 * browser, a Vue runtime, or a network. apiStatus.js owns the state machine and the
 * probing; this file owns only the two decisions that are easy to get subtly wrong.
 *
 * The classification is deliberately WIDE and deliberately non-committal: it answers
 * "is this worth checking?", never "is the server down?". A single request can fail for
 * reasons that have nothing to do with the server's health (one slow report hitting the
 * 30s axios timeout, a cancelled navigation), so a suspicion only ever starts a probe of
 * `/health`. The probe — not this function — decides whether the user sees anything.
 * That ordering is what keeps a routine slow query from throwing a full-screen
 * "under maintenance" panel over someone's work.
 */

/** Statuses IIS / a reverse proxy returns while the app pool behind it is down. */
export const OUTAGE_STATUSES = [502, 503, 504];

/**
 * Axios `error.code` values that mean the request never got an HTTP answer.
 * `undefined` counts too: older axios builds and non-axios rejections carry no code.
 */
const NO_RESPONSE_CODES = [
  "ERR_NETWORK",
  "ECONNABORTED", // axios timeout
  "ECONNREFUSED",
  "ECONNRESET",
  "ETIMEDOUT",
  "EAI_AGAIN",
];

/** Cancelled requests are our own doing — never a symptom of anything. */
const CANCEL_CODES = ["ERR_CANCELED", "ECONNABORTED_CANCELLED"];

export function isOutageStatus(status) {
  return OUTAGE_STATUSES.includes(Number(status));
}

/**
 * @param {any} error axios error (or anything a rejected interceptor was handed)
 * @returns {"suspect"|"ignore"} `"suspect"` means: probe /health before concluding anything.
 */
export function classifyApiFailure(error) {
  if (!error) return "ignore";

  // Per-request opt-out, for callers that expect and handle their own failures.
  if (error.config?.meta?.ignoreOutage) return "ignore";

  const code = error.code;
  if (CANCEL_CODES.includes(code)) return "ignore";
  // axios sets this on `CanceledError`; also covers a manual AbortController abort.
  if (error.name === "CanceledError" || error.name === "AbortError") return "ignore";

  const status = error.response?.status;
  if (status != null) {
    // The server answered. A 4xx proves the API is up and routing (a validation or
    // permission error is the caller's business). Gateway statuses mean the app pool
    // behind IIS is down. A 5xx is usually one buggy endpoint — but it is also what IIS
    // answers for EVERY request when the API failed to start after a deploy (ANCM
    // 500.30), and only the probe can tell those apart. So a 5xx is a suspicion, never
    // a verdict: /health decides.
    return isOutageStatus(status) || Number(status) >= 500 ? "suspect" : "ignore";
  }

  // No HTTP response at all: connection refused, DNS gone, TLS reset, or a timeout.
  if (code == null || NO_RESPONSE_CODES.includes(code)) return "suspect";
  return "ignore";
}

/** Body status of the API's startup-failure responder. Keep in step with StartupFailureNotice.Status. */
export const STARTUP_FAILED_STATUS = "startup-failed";

/**
 * How often an idle, visible tab checks /health. Without it the panel only ever reacted to a
 * request the page happened to make, so a user sitting on a loaded page saw nothing until they
 * clicked or refreshed. /health is anonymous and touches no database.
 */
export const HEARTBEAT_MS = 20000;

/**
 * Whether an idle heartbeat should run now: only while things look fine (the probe ladder owns
 * every other state) and only for a tab someone can see.
 *
 * @param {string} status apiStatus value
 * @param {string} [visibility] document.visibilityState
 */
export function shouldHeartbeat(status, visibility) {
  return status === "online" && visibility !== "hidden";
}

/** sessionStorage key for the answering streak, so a refresh does not restart the clock. */
export const ANSWERING_SINCE_KEY = "apiOutage:answeringSince";

/** A remembered streak older than this belongs to some earlier outage, not the current one. */
export const ANSWERING_MEMORY_MS = 30 * 60 * 1000;

/**
 * Restore the answering streak after a page load. Users refresh during outages, and restarting
 * the three-minute clock on every refresh meant a crash could hide as an update indefinitely.
 *
 * @param {string|null|undefined} raw the stored epoch-ms value
 * @param {number} now epoch ms
 * @returns {number|null}
 */
export function restoreAnsweringSince(raw, now) {
  const since = Number.parseInt(raw, 10);
  if (!Number.isFinite(since)) return null;
  const age = now - since;
  return age >= 0 && age <= ANSWERING_MEMORY_MS ? since : null;
}

/** Text IIS's ASP.NET Core Module puts in its own failure pages (500.3x / 502.5). */
const ANCM_BODY = /ANCM|Startup Failure|Process Failure/i;

/**
 * What a FAILED /health probe says about the server. Called only after the probe has
 * actually failed, so "fine" is not an answer here.
 *
 * - `"updating"` — nobody answered, or IIS answered 502/503/504: the app pool is
 *   stopped (msdeploy's AppOffline) or Kestrel is not listening yet. Normal mid-deploy.
 * - `"errored"`  — the server ANSWERED, just not with a 200: IIS's 500.30 "ANCM
 *   In-Process Start Failure" page after the API crashed on startup, a 404 from a
 *   mis-deployed site, a 401/403 from someone gating the liveness route. The deploy
 *   will not finish by itself, and the notice must read differently so an operator's
 *   report tells the developer which of the two they are looking at.
 *
 * The device being offline is judged by the caller from `navigator.onLine` before the
 * probe is even sent, so it never reaches here.
 *
 * @param {any} error the axios rejection from the probe
 * @returns {"updating"|"errored"}
 */
export function classifyProbeFailure(error) {
  const status = error?.response?.status;
  if (status == null) return "updating";
  // The API's own startup-failure responder (StartupFailureNotice.cs): a 503 that says, with
  // CORS headers the browser honours, that the app started and its startup work threw.
  const data = error.response?.data;
  if (data && typeof data === "object" && data.status === STARTUP_FAILED_STATUS) return "errored";
  if (!isOutageStatus(status)) return "errored";
  // Out-of-process hosting reports a process that would not start as 502.5 with the
  // module's own page as the body — a gateway status carrying an app failure.
  const body = error.response?.data;
  return typeof body === "string" && ANCM_BODY.test(body) ? "errored" : "updating";
}

/**
 * How long the server may keep answering WITHOUT the app before the notice changes.
 *
 * Every SPA calls its API on another host (ansh.prfa.in → anshapi.prfa.in), and IIS's own
 * pages carry none of the app's CORS headers — so a cross-origin page cannot read their
 * status, and `classifyProbeFailure` only ever sees a network error. What the page CAN see
 * is THAT something answered (a no-cors request resolves). Behind IIS two states answer
 * instantly: an app pool stopped for the file sync (app_offline, 503) and an app that crashed
 * on startup (500.30). The difference is time — a sync is over in seconds to a minute or two,
 * a crash stays until somebody acts. A slow startup never counts: ANCM holds those requests
 * open while migrations run, so nothing answers.
 */
export const ERRORED_AFTER_MS = 3 * 60 * 1000;

/**
 * Start the clock the first time the server answers without the app, keep it while that
 * continues, and drop it the moment nothing answers.
 *
 * @param {number|null} since when the current answering streak began
 * @param {boolean} answered whether this probe round got any HTTP response at all
 * @param {number} now epoch ms
 * @returns {number|null}
 */
export function nextAnsweringSince(since, answered, now) {
  if (!answered) return null;
  return Number.isFinite(since) ? since : now;
}

/**
 * The reason the panel shows for a failed probe round.
 *
 * @param {"updating"|"errored"} verdict what `classifyProbeFailure` could read
 * @param {number|null} answeringSince from `nextAnsweringSince`
 * @param {number} now epoch ms
 * @returns {"server"|"errored"}
 */
export function outageReasonFor(verdict, answeringSince, now) {
  if (verdict === "errored") return "errored"; // readable failure: no need to wait
  if (Number.isFinite(answeringSince) && now - answeringSince >= ERRORED_AFTER_MS) return "errored";
  return "server";
}

/**
 * The short code the outage panel prints after "Reconnecting automatically", so an
 * operator can read it out without being told anything alarming. deploy/README.md
 * carries the table that maps each code back to what happened on the server.
 *
 * @param {string} reason apiStatus's outage reason: "server" | "errored" | "network"
 */
export function outageNoticeCode(reason) {
  if (reason === "errored") return "UPD-2";
  if (reason === "network") return "NET-1";
  return "UPD-1";
}

/**
 * Whether the outage panel offers its game. The switch is Project Settings → Settings →
 * Platform → "Show a game while the app is updating" (`system` / `ShowGameDuringUpdate`),
 * served anonymously by `GET /auth/login-config`. It is needed exactly when the API cannot
 * answer, so the last answer is kept in the browser under this key.
 */
export const OUTAGE_GAME_CACHE_KEY = "apiOutage:showGame";

/**
 * Cached value → flag. Anything but an explicit "false" shows the game: a browser that has
 * never reached the API (first visit lands mid-deploy) gets the setting's own default.
 *
 * @param {string|null|undefined} cached the raw localStorage value
 */
export function resolveShowGame(cached) {
  return cached !== "false";
}

/**
 * login-config response → the string to cache, or `null` to leave the cache untouched. An
 * API older than the setting sends no flag, and must not wipe an answer a newer one gave.
 *
 * @param {any} config the parsed login-config body
 * @returns {"true"|"false"|null}
 */
export function showGameCacheValue(config) {
  const value = config?.showGameDuringUpdate;
  return typeof value === "boolean" ? String(value) : null;
}

/**
 * Reconnect backoff. Deploys finish in tens of seconds, so the ladder starts fast and
 * settles at a 15s ceiling rather than growing without bound — an operator staring at
 * the overlay should never wait minutes past the moment the API came back.
 */
export const PROBE_DELAYS_MS = [2000, 3000, 5000, 8000, 12000, 15000];

/**
 * @param {number} attempt zero-based count of probes already made in this outage
 * @returns {number} milliseconds to wait before the next probe
 */
export function nextProbeDelay(attempt) {
  const n = Number.isFinite(attempt) && attempt > 0 ? Math.floor(attempt) : 0;
  return PROBE_DELAYS_MS[Math.min(n, PROBE_DELAYS_MS.length - 1)];
}
